import { conversationEvidence, conversationPhase, summarizeConversation, type ConversationState } from './reducer.ts'
import {guidanceRevision} from './undo.ts'

type BoundaryState = ConversationState & {guidanceExternalBoundary?: {reason:string;pending:boolean}}
export function captureGuidanceExternalBoundary(state: ConversationState): ConversationState {
  const journal = state.guidanceUndo
  return journal?.boundary ? {...state,guidanceExternalBoundary:{reason:journal.boundary,pending:journal.externalPending}} as BoundaryState : state
}
export function restoreGuidanceExternalBoundary(state: ConversationState): ConversationState {
  const boundary = (state as BoundaryState).guidanceExternalBoundary
  if (boundary === undefined) return state
  if (!boundary || typeof boundary !== 'object' || typeof boundary.reason !== 'string' || !boundary.reason.trim() || boundary.reason.length > 10000 || typeof boundary.pending !== 'boolean') throw new Error('Saved external guidance boundary is invalid. Progress was preserved.')
  if (state.guidanceUndo) {
    if (state.guidanceUndo.boundary !== boundary.reason || state.guidanceUndo.externalPending !== boundary.pending) throw new Error('Saved external guidance boundary does not match this session. Progress was preserved.')
    return state
  }
  return {...state,guidanceUndo:{version:1,revision:0,undoRevision:0,entries:[],boundary:boundary.reason,externalPending:boundary.pending}}
}

export function sessionPayload(state: ConversationState, legacyResponses: unknown[] = [], legacyAgreements: unknown[] = [], status: 'ACTIVE' | 'COMPLETED' = 'ACTIVE') {
  const responseHistory = [...legacyResponses, ...conversationEvidence(state)]
  const agreementMap = [...legacyAgreements] // Responses are observations, not fabricated agreements.
  const payload = { sessionId: state.sessionId, contactId: state.contactId, callType: 'cold-first' as const, phase: conversationPhase(state), responseHistory, agreementMap, engineState: state, status }
  const limit = status === 'ACTIVE' ? 230000 : 245000 // Reserve room for final disposition evidence.
  for (const value of [responseHistory, agreementMap, state]) if (new TextEncoder().encode(JSON.stringify(value)).byteLength > limit) throw new Error('Conversation exceeds the session storage limit. Save the outcome before continuing.')
  return payload
}

// One writer per immutable contact/session identity. New snapshots include all
// prior events, so coalescing cannot lose turns. Completion always drains ACTIVE
// writes first and cannot be followed by a late ACTIVE write.
export function createSessionWriter(write: (value: ReturnType<typeof sessionPayload>) => Promise<unknown>, acknowledgedGuidanceRevision = 0, revisionProtocol: 'cas' | 'legacy' = 'cas') {
  let pending: ReturnType<typeof sessionPayload> | null = null
  let retry: ReturnType<typeof sessionPayload> | null = null
  let running: Promise<void> | null = null
  let failure: unknown = null
  let closed = false
  let newestGuidanceRevision = -1
  const drain = async () => {
    while (retry || pending) {
      // Keep a failed request byte-equivalent for lost-response replay before
      // sending newer queued state. Bind CAS only when this lane dispatches.
      const value = retry ?? pending!
      if (retry) retry = null; else pending = null
      const clean = { ...captureGuidanceExternalBoundary(value.engineState) } as ConversationState & {guidanceWrite?:unknown}
      if (revisionProtocol === 'legacy') { delete clean.guidanceUndo; delete clean.guidanceWrite }
      const sent = revisionProtocol === 'legacy' ? {...value,engineState:clean} : value.engineState.guidanceVersion === 9 && !Object.hasOwn(value.engineState, 'guidanceWrite')
        ? { ...value, engineState: { ...clean, guidanceWrite: { expectedRevision: acknowledgedGuidanceRevision } } } : value
      try {
        const acknowledgement = await write(sent)
        if (revisionProtocol === 'cas' && sent.engineState.guidanceVersion === 9 && acknowledgement && typeof acknowledgement === 'object' && 'engine_state' in acknowledgement) {
          const saved = acknowledgement.engine_state as ConversationState | null
          if (!saved || Object.hasOwn(saved, 'guidanceWrite') || guidanceRevision(saved) !== guidanceRevision(sent.engineState)) throw new Error('Canonical guidance revision guard is unavailable or returned an unexpected revision. Reload after the guard is verified.')
        }
        failure = null
        if (revisionProtocol === 'cas' && sent.engineState.guidanceVersion === 9) acknowledgedGuidanceRevision = guidanceRevision(sent.engineState)
      } catch (error) { retry = sent; failure = error; throw error }
    }
  }
  const run = () => { if (!running) running = drain().finally(() => { running = null }); return running }
  return {
    save(value: ReturnType<typeof sessionPayload>) {
      if (closed) return Promise.reject(new Error('Call session is completed.'))
      if (value.engineState.guidanceVersion === 9) {
        const revision = guidanceRevision(value.engineState)
        if (revision < newestGuidanceRevision) return Promise.resolve()
        newestGuidanceRevision = revision
      }
      pending = value; return run()
    },
    async flush() { if (running) await running; if (retry || pending) await run(); if (failure) throw failure },
    async complete(value: ReturnType<typeof sessionPayload>) { closed = true; if (running) await running; pending = { ...value, status: 'COMPLETED' }; await run() },
    isPending: () => Boolean(retry || pending || running),
    isClosed: () => closed,
  }
}
export const activitySummary = summarizeConversation
